/**
 * The hand-off between "Quote on your page" and the composer.
 *
 * ⚠️ **Its own module, with NO imports, and that is the point.** The writer is `DiscussionButtons`,
 * which sits on the discussion page's eager path; the reader is the composer, which does not. Putting
 * these two functions in `discussionEmbedUtils` would make a static import from `DiscussionButtons`
 * drag the editor's link helpers onto the startup graph every page parses, for the sake of two
 * `sessionStorage` calls — the growth `scripts/check-bundle-budget.mjs` exists to refuse (rule 27).
 *
 * Why sessionStorage rather than a query parameter: the composer entry route `/create/discussion`
 * 302s to `/create/discussion/{draftPublicId}` after reusing or creating a draft, and a redirect drops
 * the query string. Threading it through the server would mean a new parameter on two controller
 * methods and the redirect that joins them, to carry a value that is per-tab, transient, and
 * meaningless to anybody but this browser.
 *
 * Cleared on a TERMINAL outcome, never on a mere read. `peekPendingQuote` leaves the stash in place and
 * `clearPendingQuote` is called once the card is inserted or the post proves un-quotable, so a member who
 * navigates away from the composer and comes back does not get a second copy of the same card. It was
 * read-once-and-clear until 2026-09-13, and that lost the card whenever the composer's draft bootstrap
 * remounted the editor while the snapshot was still in flight: the first editor instance had consumed the
 * stash, its effect was cancelled by the remount, and the second instance found nothing to resolve (gate
 * `b7708e85e8`, three of four attempts, design §10.2). Every access is wrapped: a private window, cleared
 * site data, or a browser configured to block storage makes the accessor itself throw, and a share button
 * must never become a crash.
 *
 * Design: designs/01-now/2026-09-03-quote-post-design.md §10.2.
 */
const PENDING_QUOTE_KEY = "seavien.pendingQuoteDiscussionPublicId";

export function stashPendingQuote(discussionPublicId: string): void {
    try {
        window.sessionStorage.setItem(PENDING_QUOTE_KEY, discussionPublicId);
    } catch {
        // Storage unavailable. The member still lands in the composer; they just paste the link.
    }
}

export function peekPendingQuote(): string | null {
    try {
        const value = window.sessionStorage.getItem(PENDING_QUOTE_KEY);
        return value && value.trim() ? value.trim() : null;
    } catch {
        return null;
    }
}

export function clearPendingQuote(): void {
    try {
        window.sessionStorage.removeItem(PENDING_QUOTE_KEY);
    } catch {
        // Storage unavailable: there was nothing to clear.
    }
}
